/**
 * Sysadmin Platform — parent layout (Wave 3 §10.2 #5 / spec §3.7).
 */

import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { Server, Package, Key, FileKey2, Archive, Wifi, Activity } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/platform')({
  component: PlatformLayout,
});

const SUB_TABS = [
  { key: 'deployment',        label: 'Deployment',        icon: Server,   to: '/sysadmin/platform/deployment',        status: 'v1' },
  { key: 'updates',           label: 'Updates',           icon: Package,  to: '/sysadmin/platform/updates',           status: 'v2' },
  { key: 'keys',              label: 'Keys',              icon: Key,      to: '/sysadmin/platform/keys',              status: 'v1' },
  { key: 'license',           label: 'License',           icon: FileKey2, to: '/sysadmin/platform/license',           status: 'v2' },
  { key: 'backups',           label: 'Backups',           icon: Archive,  to: '/sysadmin/platform/backups',           status: 'v2' },
  { key: 'airgap',            label: 'Air-Gap',           icon: Wifi,     to: '/sysadmin/platform/airgap',            status: 'v1' },
  // AUTO-6 — Builder observability surfaces the live signal from the
  // 6 system builders (Ask Argus / Architect / Tool / Skill / Prompt /
  // Function helpers). Lands in the Platform rail next to Air-Gap so
  // it sits with the other "operational visibility" tools.
  { key: 'builder-analytics', label: 'Builder Analytics', icon: Activity, to: '/sysadmin/platform/builder-analytics', status: 'v1' },
] as const;

function PlatformLayout() {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      <div className="border-b sticky top-0 bg-background z-10">
        <nav className="flex items-center gap-0.5 overflow-x-auto px-6">
          {SUB_TABS.map((t) => {
            const Icon = t.icon;
            const active = location.pathname === t.to ||
              location.pathname.startsWith(t.to + '/');
            return (
              <Link
                key={t.key}
                to={t.to}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {t.status !== 'v1' && (
                  <Badge variant="secondary" className="ml-1 text-[10px]">
                    {t.status}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
